import React, { useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { cssMap } from '@atlaskit/css';
import {
	attachClosestEdge,
	type Edge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
	type ElementDropTargetEventBasePayload,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { Box } from '@atlaskit/primitives/compiled';

import { AppTile } from '../../../../common/ui/app-tile';
import { useKeyboardReorderContext } from '../../../../controllers/keyboard-reorder-provider';
import { DragHandle } from '../drag-handle';
import { DropIndicator } from '../drop-indicator';
import { RIBBON_DND_TILE_TYPE, type RibbonAppTileDndData } from '../types';
import { isRibbonAppTileDndData } from '../utils';

import type { ReorderableAppTileProps } from './types';

const styles = cssMap({
	reorderTileWrapper: {
		position: 'relative',
		userSelect: 'none',
		cursor: 'grab',
		'&:active': {
			cursor: 'grabbing',
		},
		width: '100%',
	},
	reorderTileWrapperDragging: {
		position: 'relative',
		userSelect: 'none',
	},
});

/**
 * A Ribbon app tile wrapped with Pragmatic DnD behaviors:
 *
 * - Draggable: creates a custom native drag preview (`previewContainer`).
 * - Drop target: accepts drops from other app tiles to compute reorder position.
 * - Hover-only drag handle: shown on mouse hover to indicate reorder affordance.
 */
export function ReorderableAppTile({
	app,
	index,
	selectedApp,
	isDragging,
	testId = '',
}: ReorderableAppTileProps): React.JSX.Element {
	const ref = useRef<HTMLDivElement | null>(null);
	const [previewContainer, setPreviewContainer] = useState<HTMLElement | null>(null);
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const [isHovered, setIsHovered] = useState(false);

	const { isKeyboardReorderModeEnabled } = useKeyboardReorderContext();

	const data: RibbonAppTileDndData = useMemo(
		() => ({
			type: RIBBON_DND_TILE_TYPE.APP_TILE,
			productId: app.productId,
			index,
		}),
		[app.productId, index],
	);

	useEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}

		function onChange({ self, source }: ElementDropTargetEventBasePayload) {
			if (source.element === element) {
				setClosestEdge(null);
				return;
			}
			setClosestEdge(extractClosestEdge(self.data));
		}

		return combine(
			draggable({
				element,
				getInitialData: () => data,
				onGenerateDragPreview({ nativeSetDragImage, location, source }) {
					setCustomNativeDragPreview({
						nativeSetDragImage,
						getOffset: preserveOffsetOnSource({
							element: source.element,
							input: location.current.input,
						}),
						render({ container }) {
							setPreviewContainer(container);
							return () => setPreviewContainer(null);
						},
					});
				},
				onDrop() {
					setPreviewContainer(null);
					// this makes the hover state disappear on drop as dropping the tile
					// sometimes does not trigger the onMouseLeave depending on the cursor position
					setIsHovered(false);
				},
			}),
			dropTargetForElements({
				element,
				canDrop({ source }) {
					// Can't drop on self. Also ensures we only accept ribbon app tiles.
					return isRibbonAppTileDndData(source.data) && source.data.productId !== app.productId;
				},
				getData({ input }) {
					// Attach the closest edge instruction so the monitor can compute the destination index.
					return attachClosestEdge(data, {
						element,
						input,
						allowedEdges: ['top', 'bottom'],
					});
				},
				getIsSticky: () => true,
				onDrag: onChange,
				onDragEnter: onChange,
				onDragLeave() {
					setClosestEdge(null);
				},
				onDrop() {
					setClosestEdge(null);
				},
			}),
		);
	}, [app.productId, data]);

	return (
		<Box
			ref={ref}
			xcss={isDragging ? styles.reorderTileWrapperDragging : styles.reorderTileWrapper}
			testId={`${testId}--reorder-tile-${app.productId}`}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{/* Drag Handle */}
			{((isHovered && !isDragging) || isKeyboardReorderModeEnabled) && (
				<DragHandle testId={`${testId}--drag-handle-${app.productId}`} />
			)}

			{/* Main app tile - logo & name */}
			<AppTile
				href={app.href}
				productId={app.productId}
				selectedApp={selectedApp}
				isDragging={isDragging}
				hideNotch={isHovered}
				isHovered={isHovered}
			/>

			{/* Dragged tile - preview */}
			{previewContainer &&
				createPortal(
					<AppTile
						href={app.href}
						productId={app.productId}
						selectedApp={selectedApp}
						hideNotch
						isDragPreview
					/>,
					previewContainer,
				)}

			{/* Drop indicator edge */}
			{closestEdge && (
				<DropIndicator testId={`${testId}--drop-edge-${app.productId}`} closestEdge={closestEdge} />
			)}
		</Box>
	);
}
